import { Box, Typography } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { FC } from 'react';
import {
  SectionHeading,
  useNoteSectionTitleInCardHeader,
} from 'src/features/visits/shared/components/NoteSectionHeading';
import { useApiClients } from 'src/hooks/useAppClients';
import { formatPhoneNumberDisplay, formatZipcodeForDisplay } from 'utils/lib/helpers/helpers';
import { useVisitNote } from '../../../hooks/useVisitNote';
import { PrescribedMedicationReviewItem } from './PrescribedMedicationReviewItem';

export const PrescribedMedicationsContainer: FC = () => {
  const titleInCardHeader = useNoteSectionTitleInCardHeader();
  const { oystehr } = useApiClients();
  const { data: note } = useVisitNote();

  const prescriptions = note?.plan.prescribedMedications;

  const uniquePharmacyIds = [
    ...new Set((prescriptions ?? []).map((m) => m.pharmacyId).filter((id): id is string => !!id)),
  ];

  const pharmacyQueries = useQueries({
    queries: uniquePharmacyIds.map((pharmacyId) => ({
      queryKey: ['erx-pharmacy', pharmacyId],
      queryFn: () => oystehr!.erx.getPharmacy({ pharmacyId }),
      enabled: !!oystehr,
    })),
  });

  const pharmacyMap = new Map(uniquePharmacyIds.map((id, i) => [id, pharmacyQueries[i].data]));

  // Group prescriptions by pharmacyId
  const groups = new Map<string | undefined, typeof prescriptions>();
  for (const med of prescriptions ?? []) {
    const key = med.pharmacyId;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(med);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      {!titleInCardHeader && <SectionHeading>Prescriptions</SectionHeading>}
      {Array.from(groups.entries()).map(([pharmacyId, meds]) => {
        const pharmacy = pharmacyId ? pharmacyMap.get(pharmacyId) : undefined;
        return (
          <Box key={pharmacyId ?? 'no-pharmacy'} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {pharmacy && (
              <Typography>
                <Typography component="span" fontWeight={600}>
                  Pharmacy:{' '}
                </Typography>
                {[
                  pharmacy.name,
                  pharmacy.address1,
                  pharmacy.address2,
                  pharmacy.city,
                  pharmacy.state,
                  pharmacy.zipCode ? formatZipcodeForDisplay(pharmacy.zipCode) : undefined,
                  formatPhoneNumberDisplay(pharmacy.phone),
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            )}
            {meds?.map((med) => <PrescribedMedicationReviewItem key={med.resourceId || med.name} medication={med} />)}
          </Box>
        );
      })}
    </Box>
  );
};
